import WeChatMask from "@/containers/common/WeChatMask";
import useInApp from "@/hooks/useIsApp";
import { getDeepLinkParams, getWebsiteUniversalLink } from "@/utils/openApp";
import { isIos, isMac, isQQBrowser, isWeibo, isWeixin, nativeBrowserVersion } from "@/utils/parserDevice";
import { compareVersion } from "@/utils/util";
import { useTranslation } from "next-i18next";
import { resolveShareAttribution, appendShareAttributionToSearchParams } from "@/utils/shortLinkTransfer";

type Hidden = "hidden" | "webkitHidden" | "msHidden" | undefined;
type VisibilityChange = "visibilitychange" | "webkitvisibilitychange" | "msvisibilitychange" | undefined;

/**
 * @summary 打开App
 * @description
 * 	- ios universal link 「支持」
 * 	- android deepLink
 * @param {String} deepLink
 * @returns {Function} evokeApp
 * @example
 * 	const { evokeApp } = useEvokeApp();
 * 	evokeApp("rockflow://client/deeplink?target=assets");
 *
 */
export default function useEvokeApp() {
	const { t } = useTranslation("common");
	const isInApp = useInApp();

	const evokeApp = (deepLink: string, callBackUrl?: string, callBackFun?: () => void) => {
		if (typeof window === "undefined") return;
		if (isInApp) {
			handleRouteDirect(deepLink);
			return;
		}

		const isSupportUniversalLink = supportUniversalLink();

		// ios 环境 && 支持universal link
		if (isSupportUniversalLink) {
			const prefix_url = getDeepLinkParams(deepLink);
			const url = getWebsiteUniversalLink(prefix_url);

			handleRouteDirect(url);
			return;
		}

		// 不能直接打开DeepLink - 右上角弹窗
		if (!supportDeepLink() && isWeixin()) {
			// 弹窗提示
			WeChatMask.open(t("weixin-open"));
			return;
		}
		handleDeepLinkOpenApp(deepLink, callBackUrl, callBackFun);
	};

	return { evokeApp };
}

/**
 * @summary 是否支持universal link
 * @returns {Boolean}
 * @description
 *  ios
 *    - qq 浏览器禁止了 universalLink
 *    - 微博禁止了 universalLink
 *    - 微信自 7.0.5 版本放开了 Universal Link 的限制
 */
function supportUniversalLink() {
	if (typeof window === "undefined") return false;

	const isIosAndMac = isIos() || isMac();
	if (!isIosAndMac) return false;

	const unSupportBrowser = isQQBrowser() || isWeibo();
	if (unSupportBrowser) return false;

	const unSupportWeChat = isWeixin() && compareVersion(String(nativeBrowserVersion()), "7.0.5") === -1;
	if (unSupportWeChat) return false;

	return true;
}

/**
 * @summary 是否支持DeepLink
 * @description
 *   - qq 浏览器禁止了
 *   - 微博禁止了
 *   - 微信禁止了
 * @returns {Boolean}
 */
function supportDeepLink(): boolean {
	if (typeof window === "undefined") return false;

	const unSupportBrowser = isQQBrowser() || isWeibo() || isWeixin();
	if (unSupportBrowser) return false;

	return true;
}

/**
 * @summary DeepLink 打开App
 * @description
 *   - 安装App - 打开App
 *   - 未按转App - 跳转下载App页面
 * @param {String} deepLink
 * @returns {void}
 */
export function handleDeepLinkOpenApp(deepLink: string, callBackUrl?: string, callBackFun?: () => void): void {
	handleRouteDirect(deepLink);
	checkEvokeOpen(() => {
		if (callBackUrl) {
			handleRouteDirect(callBackUrl);
			return;
		}
		if (callBackFun) {
			callBackFun();
			return;
		}
		handleRouteDirect(directDownloadAddress(deepLink));
	});
}

/**
 * @summary 检查是否打开App
 */
function checkEvokeOpen(failCall: () => void): void {
	const { hidden, visibilityChange } = getProperty();
	const timer = setTimeout(() => {
		const pageHidden = pageIsHidden(hidden);
		if (!pageHidden) {
			failCall();
		}
	}, 2000);

	if (typeof visibilityChange !== "undefined") {
		document.addEventListener(visibilityChange, () => {
			clearTimeout(timer);
		});
	} else {
		window.addEventListener("pagehide", () => {
			clearTimeout(timer);
		});
	}
}

/**
 * @summary 判断页面是否隐藏
 * @param {Hidden} hidden
 * @returns {Boolean}
 */
function pageIsHidden(hidden: Hidden): boolean {
	if (typeof document === "undefined") return false;
	if (typeof hidden === "undefined") return false;
	return (document as any)[hidden] as boolean;
}

/**
 * @summary 获取页面隐藏属性 && 隐藏事件
 */
function getProperty(): { hidden: Hidden; visibilityChange: VisibilityChange } {
	let hidden: Hidden;
	let visibilityChange: VisibilityChange;

	if (typeof document === "undefined") return { hidden, visibilityChange };

	if (typeof document.hidden !== "undefined") {
		hidden = "hidden";
		visibilityChange = "visibilitychange";
	} else if (typeof (document as any).msHidden !== "undefined") {
		hidden = "msHidden";
		visibilityChange = "msvisibilitychange";
	} else if (typeof (document as any).webkitHidden !== "undefined") {
		hidden = "webkitHidden";
		visibilityChange = "webkitvisibilitychange";
	}

	return { hidden, visibilityChange };
}

/**
 * @summary 下载地址
 * @param {String} deepLink
 * @returns {String}
 */
function directDownloadAddress(deepLink: string): string {
	const pathname = window.location.pathname;
	const locale = getLocaleFromPathname(pathname);
	const params = new URLSearchParams();
	params.set("open", "0");
	params.set("deeplink", deepLink);

	const attr = resolveShareAttribution();

	// deeplink 内再兜一层，防止落盘前点击
	if (!attr.campaign) {
		const match = deepLink.match(/[?&](?:c|inviteCode)=([^&]+)/);
		if (match?.[1]) attr.campaign = decodeURIComponent(match[1]);
	}
	if (!attr.mediaSource) {
		const match = deepLink.match(/[?&]mediaSource=([^&]+)/);
		if (match?.[1]) attr.mediaSource = decodeURIComponent(match[1]);
	}
	if (!attr.channel) {
		const match = deepLink.match(/[?&]channel=([^&]+)/);
		if (match?.[1]) attr.channel = decodeURIComponent(match[1]);
	}
	if (!attr.e) {
		const match = deepLink.match(/[?&]e=([^&]+)/);
		if (match?.[1]) attr.e = decodeURIComponent(match[1]);
	}
	if (!attr.p) {
		const match = deepLink.match(/[?&]p=([^&]+)/);
		if (match?.[1]) attr.p = decodeURIComponent(match[1]);
	}
	if (!attr.i) {
		const match = deepLink.match(/[?&]i=([^&]+)/);
		if (match?.[1]) attr.i = decodeURIComponent(match[1]);
	}

	appendShareAttributionToSearchParams(params, attr);

	return `${window.location.origin.replace(".ai", ".tech")}/${locale}/intents/download?${params.toString()}`;
}

/**
 * @summary App下载地址
 * @param {String} deepLink
 * @returns {String}
 */
function getLocaleFromPathname(pathname: string): string {
	const _pathname = pathname.toLocaleLowerCase();
	const locales = [
		{ locale: "en", path: "/en/" },
		{ locale: "zh-Hans", path: "/zh-hans/" },
		{ locale: "zh-Hant", path: "/zh-hant/" },
		{ locale: "vi", path: "/vi/" },
		{ locale: "id", path: "/id/" },
		{ locale: "th", path: "/th/" },
		{ locale: "es", path: "/es/" },
		{ locale: "pt", path: "/pt/" },
		{ locale: "ko", path: "/ko/" },
		{ locale: "ja", path: "/ja/" },
		{ locale: "jp", path: "/jp/" },
		{ locale: "zh-HK", path: "/zh-hk/" },
	];

	for (let i = 0; i < locales.length; i++) {
		const option = locales[i];
		if (_pathname.startsWith(option.path)) return option.locale;
	}

	return "en";
}

/**
 * @summary 地址跳转
 * @param {String} url
 */
function handleRouteDirect(url: string): void {
	if (typeof window === "undefined") return;
	if (window.top) {
		window.top.location.href = url;
	} else {
		window.location.href = url;
	}
}
